import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { Loader2, Save, AlertTriangle, CheckCircle, Copy } from 'lucide-react';
import { toast } from 'sonner';
import { repairsApi } from '@/services/api';

const SaveToMasterModal = ({
  isOpen,
  onClose,
  repair,
  repairParts = [],
  vehicleMake = '',
  carType = 'sedan',
  onSaveComplete,
  countryId = 'c49e1dc6-1450-40c2-9846-56b73369b2b1'
}) => {
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [existingPart, setExistingPart] = useState(null);
  const [checkingPart, setCheckingPart] = useState(false);
  const [qaCategories, setQaCategories] = useState([]);
  const [selectedQaCategory, setSelectedQaCategory] = useState('');
  const [questions, setQuestions] = useState([]);
  const [selectedQuestion, setSelectedQuestion] = useState('');
  const [loadingQuestions, setLoadingQuestions] = useState(false);
  const [pricingType, setPricingType] = useState('generic');
  const [pricingData, setPricingData] = useState({
    hatchback: { repair_price: 0, replace_price: 0, repair_labor: 0, replace_labor: 0 },
    sedan: { repair_price: 0, replace_price: 0, repair_labor: 0, replace_labor: 0 },
    suv: { repair_price: 0, replace_price: 0, repair_labor: 0, replace_labor: 0 }
  });
  const [ruleCondition, setRuleCondition] = useState('poor');
  const [ruleAction, setRuleAction] = useState('repair');
  const [rulePriority, setRulePriority] = useState('normal');
  const [existingRule, setExistingRule] = useState(null);

  useEffect(() => {
    if (repair) {
      const currentPrice = repair.price || 0;
      const currentLabor = repair.labor || 0;
      const carTypeKey = carType.toLowerCase();
      setPricingData(prev => ({
        ...prev,
        [carTypeKey]: {
          repair_price: currentPrice,
          replace_price: repair.action === 'replace' ? currentPrice : 0,
          repair_labor: currentLabor,
          replace_labor: repair.action === 'replace' ? currentLabor : 0
        }
      }));
      setRuleAction(repair.action || 'repair');
      setRulePriority(repair.type === 'CRITICAL' ? 'critical' : repair.type === 'MAJOR' ? 'high' : 'normal');
    }
  }, [repair, carType]);

  useEffect(() => {
    if (isOpen) {
      loadQaCategories();
      checkExistingPart();
    }
  }, [isOpen]);

  const loadQaCategories = async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/inspection-qa/questions`);
      const cats = [...new Set((response.data || []).map(q => q.category_name))].filter(Boolean);
      setQaCategories(cats.map(c => ({ id: c, name: c })));
    } catch (err) {
      console.error('Failed to load Q&A categories:', err);
    }
  };

  const checkExistingPart = async () => {
    if (!repair?.item) return;
    setCheckingPart(true);
    try {
      const normalizedName = repair.item.toLowerCase().trim();
      const existing = repairParts.find(p => p.name?.toLowerCase().trim() === normalizedName);
      setExistingPart(existing || null);
    } finally {
      setCheckingPart(false);
    }
  };

  const loadQuestions = async (categoryName) => {
    setLoadingQuestions(true);
    try {
      const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/inspection-qa/questions`);
      const filtered = (response.data || []).filter(q => q.category_name === categoryName && q.question_text);
      setQuestions(filtered);
    } catch (err) {
      console.error('Failed to load questions:', err);
      setQuestions([]);
    } finally {
      setLoadingQuestions(false);
    }
  };

  const handleCategoryChange = (cat) => {
    setSelectedQaCategory(cat);
    setSelectedQuestion('');
    loadQuestions(cat);
  };

  const checkExistingRule = async (questionId, partId) => {
    try {
      const response = await repairsApi.getRules({ question_id: questionId });
      const rules = response.data || [];
      const existing = rules.find(r => r.part_id === partId);
      setExistingRule(existing || null);
      return existing;
    } catch (err) {
      console.error('Failed to check existing rule:', err);
      return null;
    }
  };

  const handleSave = async () => {
    setLoading(true);
    try {
      let partId = existingPart?.id || repair.part_id;
      if (!partId) {
        const newPart = {
          name: repair.item,
          category: repair.category || selectedQaCategory || 'Uncategorized',
          description: `Added from inspection repair`,
          part_number: repair.part_number || '',
          hatchback: pricingData.hatchback,
          sedan: pricingData.sedan,
          suv: pricingData.suv,
          brand_overrides: pricingType === 'brand' && vehicleMake ? [{
            brand: vehicleMake, hatchback: pricingData.hatchback, sedan: pricingData.sedan, suv: pricingData.suv
          }] : [],
          is_active: true
        };
        const partResult = await repairsApi.createPart(newPart);
        partId = partResult.data.id;
        toast.success(`Spare part "${repair.item}" created`);
      } else if (existingPart) {
        const updatedPart = { ...existingPart };
        if (pricingType === 'brand' && vehicleMake) {
          const brandOverrides = updatedPart.brand_overrides || [];
          const existingIdx = brandOverrides.findIndex(bo => bo.brand?.toLowerCase() === vehicleMake.toLowerCase());
          if (existingIdx >= 0) {
            brandOverrides[existingIdx] = { ...brandOverrides[existingIdx], brand: vehicleMake, hatchback: pricingData.hatchback, sedan: pricingData.sedan, suv: pricingData.suv };
          } else {
            brandOverrides.push({ brand: vehicleMake, hatchback: pricingData.hatchback, sedan: pricingData.sedan, suv: pricingData.suv });
          }
          updatedPart.brand_overrides = brandOverrides;
        } else {
          updatedPart.hatchback = pricingData.hatchback;
          updatedPart.sedan = pricingData.sedan;
          updatedPart.suv = pricingData.suv;
        }
        await repairsApi.updatePart(partId, updatedPart);
        toast.success(`Pricing updated for "${repair.item}"`);
      }
      if (selectedQuestion && partId) {
        const existingRule = await checkExistingRule(selectedQuestion, partId);
        const ruleData = {
          question_id: selectedQuestion, part_id: partId,
          conditions: [{ condition: { operator: 'contains', value: ruleCondition }, action: { action_type: ruleAction, priority: rulePriority, notes: '' } }],
          is_active: true
        };
        if (existingRule) { await repairsApi.updateRule(existingRule.id, ruleData); toast.success('Repair rule updated'); }
        else { await repairsApi.createRule(ruleData); toast.success('Repair rule created'); }
      }
      onSaveComplete && onSaveComplete(partId);
      onClose();
    } catch (err) {
      console.error('Failed to save to master:', err);
      toast.error(err.response?.data?.detail || 'Failed to save to master');
    } finally {
      setLoading(false);
    }
  };

  const updatePricing = (carTypeKey, field, value) => {
    setPricingData(prev => ({ ...prev, [carTypeKey]: { ...prev[carTypeKey], [field]: parseFloat(value) || 0 } }));
  };

  const copyToAll = (sourceCarType) => {
    const source = pricingData[sourceCarType];
    setPricingData({ hatchback: { ...source }, sedan: { ...source }, suv: { ...source } });
    toast.success('Pricing copied to all car types');
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Save className="h-5 w-5 text-green-600" />
            Save to Master Data
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-6">
          <div className="p-4 bg-blue-50 rounded-lg">
            <div className="flex items-center justify-between">
              <div>
                <h4 className="font-semibold text-gray-900">{repair?.item || 'Unnamed Part'}</h4>
                <p className="text-sm text-gray-600">{repair?.category || 'No category'} • {repair?.action || 'repair'}</p>
              </div>
              <div className="text-right">
                <p className="text-lg font-bold text-green-600">Rs.{(repair?.estimated_cost || 0).toLocaleString()}</p>
                <p className="text-xs text-gray-500">Current: {carType}</p>
              </div>
            </div>
            {checkingPart ? (
              <div className="mt-3 flex items-center gap-2 text-sm text-gray-500"><Loader2 className="h-4 w-4 animate-spin" />Checking for existing part...</div>
            ) : existingPart ? (
              <div className="mt-3 p-2 bg-yellow-100 rounded text-sm text-yellow-800"><AlertTriangle className="h-4 w-4 inline mr-1" />Part "{existingPart.name}" already exists. Pricing will be updated.</div>
            ) : (
              <div className="mt-3 p-2 bg-green-100 rounded text-sm text-green-800"><CheckCircle className="h-4 w-4 inline mr-1" />New part will be created in master data.</div>
            )}
          </div>
          <div className="space-y-4">
            <h4 className="font-medium text-gray-900 flex items-center gap-2">
              <span className="h-6 w-6 rounded-full bg-blue-100 text-blue-700 text-xs flex items-center justify-center">1</span>
              Link to Inspection Q&A (Optional)
            </h4>
            <p className="text-sm text-gray-500">Select a Q&A category and question to automatically trigger this repair when answered.</p>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-sm mb-1 block">Q&A Category</Label>
                <Select value={selectedQaCategory} onValueChange={handleCategoryChange}>
                  <SelectTrigger><SelectValue placeholder="Select category..." /></SelectTrigger>
                  <SelectContent>{qaCategories.map(cat => (<SelectItem key={cat.id || cat.name || cat} value={cat.name || cat}>{cat.name || cat}</SelectItem>))}</SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-sm mb-1 block">Question</Label>
                <Select value={selectedQuestion} onValueChange={setSelectedQuestion} disabled={!selectedQaCategory || loadingQuestions}>
                  <SelectTrigger><SelectValue placeholder={loadingQuestions ? "Loading..." : "Select question..."} /></SelectTrigger>
                  <SelectContent>{questions.map(q => (<SelectItem key={q.question_id} value={q.question_id}>{q.question_text?.substring(0, 50)}...</SelectItem>))}</SelectContent>
                </Select>
              </div>
            </div>
            {selectedQuestion && (
              <div className="grid grid-cols-3 gap-3 p-3 bg-gray-50 rounded-lg">
                <div>
                  <Label className="text-xs mb-1 block">Trigger Condition</Label>
                  <Select value={ruleCondition} onValueChange={setRuleCondition}>
                    <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="poor">Answer contains "Poor"</SelectItem>
                      <SelectItem value="average">Answer contains "Average"</SelectItem>
                      <SelectItem value="damaged">Answer contains "Damaged"</SelectItem>
                      <SelectItem value="broken">Answer contains "Broken"</SelectItem>
                      <SelectItem value="worn">Answer contains "Worn"</SelectItem>
                      <SelectItem value="cracked">Answer contains "Cracked"</SelectItem>
                      <SelectItem value="leaking">Answer contains "Leaking"</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs mb-1 block">Action Type</Label>
                  <Select value={ruleAction} onValueChange={setRuleAction}>
                    <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="repair">Repair</SelectItem>
                      <SelectItem value="replace">Replace</SelectItem>
                      <SelectItem value="both">Repair + Labor</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs mb-1 block">Priority</Label>
                  <Select value={rulePriority} onValueChange={setRulePriority}>
                    <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="normal">Normal (Minor)</SelectItem>
                      <SelectItem value="high">High (Major)</SelectItem>
                      <SelectItem value="critical">Critical</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            )}
          </div>
          <div className="space-y-4">
            <h4 className="font-medium text-gray-900 flex items-center gap-2">
              <span className="h-6 w-6 rounded-full bg-blue-100 text-blue-700 text-xs flex items-center justify-center">2</span>
              Pricing Configuration
            </h4>
            <div className="flex gap-4">
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="radio" name="pricingType" value="generic" checked={pricingType === 'generic'} onChange={() => setPricingType('generic')} className="w-4 h-4" />
                <span className="text-sm">Generic Pricing (All Brands)</span>
              </label>
              {vehicleMake && (
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="radio" name="pricingType" value="brand" checked={pricingType === 'brand'} onChange={() => setPricingType('brand')} className="w-4 h-4" />
                  <span className="text-sm">Brand-Specific ({vehicleMake})</span>
                </label>
              )}
            </div>
            <div className="border rounded-lg overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-3 py-2 text-left">Car Type</th>
                    <th className="px-3 py-2 text-left">Repair Price</th>
                    <th className="px-3 py-2 text-left">Repair Labor</th>
                    <th className="px-3 py-2 text-left">Replace Price</th>
                    <th className="px-3 py-2 text-left">Replace Labor</th>
                    <th className="px-3 py-2"></th>
                  </tr>
                </thead>
                <tbody>
                  {['hatchback', 'sedan', 'suv'].map(ct => (
                    <tr key={ct} className={ct === carType.toLowerCase() ? 'bg-blue-50' : ''}>
                      <td className="px-3 py-2 font-medium capitalize">{ct}{ct === carType.toLowerCase() && <span className="ml-1 text-xs text-blue-600">(Current)</span>}</td>
                      <td className="px-2 py-1"><Input type="number" value={pricingData[ct].repair_price || ''} onChange={(e) => updatePricing(ct, 'repair_price', e.target.value)} className="h-8 w-24 text-xs" placeholder="0" /></td>
                      <td className="px-2 py-1"><Input type="number" value={pricingData[ct].repair_labor || ''} onChange={(e) => updatePricing(ct, 'repair_labor', e.target.value)} className="h-8 w-24 text-xs" placeholder="0" /></td>
                      <td className="px-2 py-1"><Input type="number" value={pricingData[ct].replace_price || ''} onChange={(e) => updatePricing(ct, 'replace_price', e.target.value)} className="h-8 w-24 text-xs" placeholder="0" /></td>
                      <td className="px-2 py-1"><Input type="number" value={pricingData[ct].replace_labor || ''} onChange={(e) => updatePricing(ct, 'replace_labor', e.target.value)} className="h-8 w-24 text-xs" placeholder="0" /></td>
                      <td className="px-2 py-1"><Button variant="ghost" size="sm" onClick={() => copyToAll(ct)} className="h-7 text-xs" title="Copy to all car types"><Copy className="h-3 w-3" /></Button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
          <div className="p-4 bg-gray-50 rounded-lg space-y-2">
            <h4 className="font-medium text-gray-900">Summary</h4>
            <ul className="text-sm text-gray-600 space-y-1">
              <li>• {existingPart ? 'Update' : 'Create'} spare part: <strong>{repair?.item}</strong></li>
              <li>• Pricing type: <strong>{pricingType === 'brand' ? `Brand-specific (${vehicleMake})` : 'Generic'}</strong></li>
              {selectedQuestion && (<li>• Create repair rule: <strong>When answer contains "{ruleCondition}" → {ruleAction}</strong></li>)}
            </ul>
          </div>
          <div className="flex justify-end gap-3">
            <Button variant="outline" onClick={onClose}>Cancel</Button>
            <Button onClick={handleSave} disabled={loading} className="bg-green-600 hover:bg-green-700">
              {loading ? (<><Loader2 className="h-4 w-4 animate-spin mr-2" />Saving...</>) : (<><Save className="h-4 w-4 mr-2" />Save to Master</>)}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default SaveToMasterModal;
